import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HartlepoolCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Hartlepool Pothole Claims | 33% Estate Roads Failed in 2022 | Fixtyer"
        description="Hartlepool Borough Council recorded a 33% failure rate on unclassified roads in 2022 — and declined a re-survey. 80% of its 411km network is housing estates. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/hartlepool"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Hartlepool', url: '/councils/hartlepool' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Hartlepool has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. Hartlepool projects capital spend matching its £2.187m DfT allocation in 2025/26 and targets 72% preventative maintenance. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. The DfT Condition scorecard is AMBER, and the council\'s own CVI data shows unclassified RED failure spiking to 33% in 2022 before settling at 7% in 2024.',
          },
          {
            question: 'What if my pothole was on a housing estate or residential street?',
            answer: 'Unclassified roads make up 328km — roughly 80% of Hartlepool\'s 411km carriageway network. The council describes these as roads "basically mak[ing] up the housing estates throughout the town." CVI surveys produce a RED-equivalent failure percentage for U-roads. At the latest survey, 7% were in the RED category — but the council itself notes the 33% figure for 2022 was an anomaly it declined to re-verify.',
          },
          {
            question: 'Does the 33% U-road failure rate in 2022 help my claim?',
            answer: 'Potentially, if your incident was in or around 2022/23. Hartlepool\'s report states the 33% return "represents a bit of an anomaly" repeated across Tees Valley, was reported to the survey contractor, and a full re-survey was offered — but "the offer being declined" because timing was close to the following year\'s survey. That is documented uncertainty about network condition at a point when one in three estate roads was recorded as failed.',
          },
          {
            question: 'Pothole jobs fell from 2,730 to 1,725 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Hartlepool raised 9,711 pothole jobs across 2021/22 to 2024/25, and the council itself notes repair volumes are "very weather dependent." The 2025/26 estimate is 2,427 jobs. Reactive maintenance still accounts for an estimated 28% of annual spend. Falling job counts and AMBER condition ratings can coexist.',
          },
          {
            question: 'Why is Hartlepool AMBER on best practice despite its Tarmac partnership?',
            answer: 'The DfT Best Practice scorecard is AMBER — not GREEN — even though Hartlepool highlights a long-standing Tarmac contract, carbon-reducing resurfacing materials, and PAS2161 transition planning from 2026/27. Best practice ratings turn on published asset management evidence and inspection coverage. The council\'s asset management system does not allow easy extraction of pothole quantities, relying instead on "pothole jobs" raised by inspectors.',
          },
          {
            question: 'How does Section 58 apply to Hartlepool\'s inspection regime?',
            answer: 'Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance systems. Hartlepool SCANNER-surveys all A, B and C roads annually at 100% coverage, but U-roads use coarse visual inspection with a RED-equivalent failure metric. Your claim succeeds when evidence shows the specific defect met intervention criteria, was previously reported, or should have been found before you hit it — especially where CVI data or the 2022 anomaly raises questions about what the council actually knew.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Hartlepool Borough Council"
        councilSlug="hartlepool"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/hartlepool"
        headline="Hartlepool Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Hartlepool</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Hartlepool: One in Three Estate Roads Failed — Re-Survey Declined
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Hartlepool Borough Council matches its DfT capital allocation pound for pound and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">33% of unclassified roads were recorded as failed in 2022</strong>, a
            figure the council reported as an anomaly, offered to re-survey, and then declined — on a network where{' '}
            <strong className="text-rose-400">328km of housing-estate roads make up 80% of the borough</strong>.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">33%</div>
            <div className="text-white font-medium mt-2">U-roads in RED category (2022)</div>
            <div className="text-brand-300 text-sm mt-1">
              Hartlepool reported the spike to its survey contractor and was offered a full re-survey — but
              declined because timing was close to the following year's survey. Latest CVI data: 7% (2024).
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER survey data from Hartlepool's own transparency report — classified roads surveyed at 100%
            annually across 83km of A, B and C carriageways
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (52km — 12.7% of network): steady at 3% RED</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">flat since 2020 (3–4%)</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">16%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 21% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">81%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 76% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Hartlepool's principal network has held RED sections in a "steady state" — but skid resistance
              data tells a different story on surface safety (see below).
            </p>
          </div>

          {/* B roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B roads: volatile amber band</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">27%</td>
                    <td className="px-4 py-2 text-center">67%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">8%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">33%</td>
                    <td className="px-4 py-2 text-center">59%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">71%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">4%</td>
                    <td className="px-4 py-2 text-center font-bold">20%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">76%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B-road amber peaked at 33% in 2021 before recovering. The council notes RED sections remain steady
              while amber sections cross into RED as planned maintenance completes elsewhere.
            </p>
          </div>

          {/* C roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">C roads: low RED, amber band holding at 25%</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">2%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">1–3% since 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">25%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">down from 33% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">73%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 65% in 2020</div>
              </div>
            </div>
            <p className="text-emerald-800 text-sm">
              Classified roads are surveyed annually at 100% coverage — a stronger inspection baseline than
              the estate network that makes up four-fifths of Hartlepool's carriageways.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Spend matches allocation — but reactive work persists</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£2.187m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£2.187m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">28%</div>
                <div className="text-brand-300 text-sm">Estimated reactive maintenance share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Hartlepool spends its full DfT allocation — earning a{' '}
              <span className="text-emerald-400">GREEN</span> spend scorecard — yet an estimated{' '}
              <span className="text-amber-400">28% of annual budget still goes on reactive pothole repairs</span>.
              Revenue spend for reactive works has fallen from £1.27m in 2022/23 to a projected £813,000 in
              2025/26, but the council itself notes repair volumes remain weather dependent.
            </p>
          </div>
        </div>
      </section>

      {/* The 328km Estate Network */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 328km estate network
          </h2>
          <p className="text-surface-600 mb-8">
            80% of Hartlepool's 411km carriageway network is unclassified — "basically mak[ing] up the housing
            estates throughout the town"
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED category (CVI)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600 text-lg">33%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">7%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="It is noted from the above that the 33% failure rate of 2022 represents a bit of an anomaly and conversations with colleagues over the Tees Valley seemed to imply that this scenario was repeated elsewhere. The high return was reported to the survey contractor and another full survey was offered but the timing would have been very close to the following year's survey dates which ultimately resulted in the offer being declined."
              source="Hartlepool Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">CVI vs SCANNER</h3>
              <p className="text-surface-600 text-sm mb-3">
                Hartlepool uses laser-based SCANNER surveys for A, B and C roads annually. Unclassified roads
                use coarse visual inspection (CVI) — a two-person driven survey that produces a RED-equivalent
                failure percentage rather than green/amber/red bands.
              </p>
              <p className="text-surface-600 text-sm">
                CVI data is sorted by "overall defect" in a "worst case first" format to determine the
                resurfacing programme. The council states U-roads have shown "a steady improvement" — but the
                2022 spike and declined re-survey leave a documented gap in verified condition data.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">New developments and adoption delays</h3>
              <p className="text-amber-800 text-sm mb-3">
                Hartlepool notes new housing estates (Wynyard, Upper/Middle Warren on the A179) undergo an
                adoption process that "can take several years to complete." Until formally adopted, the
                housing developer — not the council — is responsible for upkeep.
              </p>
              <p className="text-amber-800 text-sm">
                If your pothole was on a newer estate, check whether the road had been adopted into the
                council's scheduled inspection regime at the time of your incident.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Hartlepool's estate network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road on a Wynyard or Warren development still awaiting adoption?</li>
              <li>• If the 2022 survey recorded 33% failure, what did the council do before declining a re-survey?</li>
              <li>• Does CVI coverage match the 328km of U-roads the council is responsible for maintaining?</li>
              <li>• How does a "pothole job" count relate to actual defects — when one job can cover multiple potholes?</li>
            </ul>
            <p className="text-white font-medium">
              Documented uncertainty about a one-in-three failure rate is not the same as knowing your
              network is in good repair.
            </p>
          </div>
        </div>
      </section>

      {/* Skid Resistance */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A-road skid resistance: investigation need doubled
          </h2>
          <p className="text-surface-600 mb-8">
            Tees Valley joint skid resistance surveys on Hartlepool's 52km A-road network — separate from
            SCANNER structural condition
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">A-road network flagged for further investigation</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">18.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">36.1%</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">38.7%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Not the same as pothole depth</h3>
              <p className="text-surface-600 text-sm">
                Skid resistance measures surface friction — relevant to grip and surface treatment needs, not
                necessarily the structural pothole that damaged your vehicle. But the council uses this data
                alongside SCANNER to determine maintenance on the A689, A179 and other principal routes.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Seasonal adjustment caveat</h3>
              <p className="text-amber-800 text-sm">
                Hartlepool notes skid results are "very dependent upon the time of the year the survey is
                carried out" and that Tees Valley councils survey at different times on a four-year rotation.
                Year-on-year comparisons require seasonal adjustment — the council's own report flags this
                limitation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole Jobs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            9,711 pothole jobs in four years
          </h2>
          <p className="text-surface-600 mb-8">
            Hartlepool counts "pothole jobs" raised by highway inspectors — not individual potholes
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Pothole jobs raised</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Resurfacing implemented (miles)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,585</td>
                  <td className="px-6 py-4 text-center">5.3</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,671</td>
                  <td className="px-6 py-4 text-center">6.9</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,730</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">9.8</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">1,725</td>
                  <td className="px-6 py-4 text-center">6.4</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025/26 (estimate)</td>
                  <td className="px-6 py-4 text-center">2,427</td>
                  <td className="px-6 py-4 text-center">5.6 (to date)</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total (2021/22–2024/25)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9,711</td>
                  <td className="px-6 py-4 text-center font-bold">28.4</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The counting caveat</h3>
              <p className="text-surface-600 text-sm">
                Hartlepool's asset management system "does not allow easy extraction of pothole quantities."
                The published figures are "pothole jobs" — and "a pothole job may include more than one
                pothole if such quantities are identified outside the same location." The true defect count
                is therefore higher than the table shows.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Weather-dependent trend</h3>
              <p className="text-amber-800 text-sm">
                The council states the trend has shown a "downward trend in the recent four or five years"
                and hopes to fall below 1,700 jobs per year — but adds this "can be very weather dependent."
                A drop from 2,730 to 1,725 in one year does not prove the network is fixed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            411km of roads — and 7,000 streetworks permits
          </h2>
          <p className="text-surface-600 mb-8">
            Hartlepool's network scale and the coordination burden on its highways team
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">411km</div>
              <div className="text-sm text-surface-600 mt-1">Total carriageways</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">593km</div>
              <div className="text-sm text-surface-600 mt-1">Footways</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-5 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">229km</div>
              <div className="text-sm text-surface-600 mt-1">Cycleways</div>
            </div>
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-200 text-center">
              <div className="text-3xl font-bold text-amber-700">7,000+</div>
              <div className="text-sm text-amber-800 mt-1">Streetworks permit applications (previous year)</div>
            </div>
          </div>

          <QuoteCard
            quote="The trend for the number of potholes repaired within Hartlepool has shown a downward trend in the recent four or five years and it is hoped, with the additional funding element, that this figure will continue to fall to below 1700 per year though, it has to be said, this can be very weather dependent."
            source="Hartlepool Borough Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against an AMBER council with GREEN spend
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Hartlepool invests its full allocation — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend matches DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 72% preventative maintenance projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% annual SCANNER coverage on all classified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED failure down to 7% in latest CVI survey (2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented five-year resurfacing programme with Tarmac partnership
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on principal A-roads with recent SCANNER data.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — DfT overall rating reflects network-wide concerns
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 33% U-road failure in 2022 with declined re-survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 328km of estate roads on a different survey method to classified routes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 9,711 pothole jobs in four years — defects still forming between repairs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — asset system cannot easily count individual potholes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Adoption delays on new estates — developer liability until formal handover
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council that matches its DfT allocation and runs structured SCANNER surveys, your claim
              lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council enquiries) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the 2022 anomaly and CVI methodology are your structural arguments</li>
              <li>• Whether the road was adopted — developer liability may apply on newer Wynyard or Warren estates</li>
              <li>• Timing relative to the 2022 CVI spike if your incident was in that period</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Hartlepool's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to Hartlepool Borough Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting creates a record the council cannot ignore — and prior reports strengthen Section 58
            rebuttals if you later claim for damage.
          </p>
          <a
            href="https://hartlepool-self.achieveservice.com/service/Online___enquiry_form?serviceHbc=Highways"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a highway defect to Hartlepool BC
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Hartlepool?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An AMBER council still owes you a safe highway. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 2022 CVI anomaly argument</li>
                <li>• No prior-report search</li>
                <li>• No adoption-status check</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 33% U-road failure and declined re-survey cited</li>
                <li>✅ 328km estate network context documented</li>
                <li>✅ 9,711 pothole jobs in four years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Hartlepool</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=hartlepool"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Hartlepool claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Hartlepool has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Hartlepool projects capital spend matching its £2.187m DfT allocation in 2025/26 and
                targets 72% preventative maintenance. Section 58 turns on whether the specific defect that
                damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. The DfT
                Condition scorecard is AMBER, and the council's own CVI data shows unclassified RED failure
                spiking to 33% in 2022 before settling at 7% in 2024.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a housing estate or residential street?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 328km — roughly 80% of Hartlepool's 411km carriageway network.
                The council describes these as roads "basically mak[ing] up the housing estates throughout the
                town." CVI surveys produce a RED-equivalent failure percentage for U-roads. At the latest survey,
                7% were in the RED category — but the council itself notes the 33% figure for 2022 was an
                anomaly it declined to re-verify.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 33% U-road failure rate in 2022 help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially, if your incident was in or around 2022/23. Hartlepool's report states the 33%
                return "represents a bit of an anomaly" repeated across Tees Valley, was reported to the survey
                contractor, and a full re-survey was offered — but "the offer being declined" because timing
                was close to the following year's survey. That is documented uncertainty about network condition
                at a point when one in three estate roads was recorded as failed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole jobs fell from 2,730 to 1,725 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Hartlepool raised 9,711 pothole jobs across 2021/22 to 2024/25, and the council itself
                notes repair volumes are "very weather dependent." The 2025/26 estimate is 2,427 jobs. Reactive
                maintenance still accounts for an estimated 28% of annual spend. Falling job counts and AMBER
                condition ratings can coexist.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Hartlepool AMBER on best practice despite its Tarmac partnership?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Best Practice scorecard is AMBER — not GREEN — even though Hartlepool highlights a
                long-standing Tarmac contract, carbon-reducing resurfacing materials, and PAS2161 transition
                planning from 2026/27. Best practice ratings turn on published asset management evidence and
                inspection coverage. The council's asset management system does not allow easy extraction of
                pothole quantities, relying instead on "pothole jobs" raised by inspectors.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Section 58 apply to Hartlepool's inspection regime?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable
                maintenance systems. Hartlepool SCANNER-surveys all A, B and C roads annually at 100% coverage,
                but U-roads use coarse visual inspection with a RED-equivalent failure metric. Your claim
                succeeds when evidence shows the specific defect met intervention criteria, was previously
                reported, or should have been found before you hit it — especially where CVI data or the 2022
                anomaly raises questions about what the council actually knew.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.hartlepool.gov.uk/downloads/file/540/local-highways-maintenance-transparency-report-updated-june-2025"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Hartlepool Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
